import { z } from 'zod';

import { NoteFacingTypeSchema } from './noteSchema.ts';
import {
  OrderTotalSchema,
  PriceSchema,
  SellTotalSchema,
  UnitSellPricingSchema,
} from './pricingSchema.ts';
import { CreateJobFromStockFormItemSchema } from './stockSchema.ts';
import { WarrantySchema } from './warrantiesSchema.ts';

///// BACKEND SCHEMAS....

const OrderCreationSourceEnum = z.enum([
  'CustomerRequest',
  'OtherJobDoingIdentified',
  'SystemDataIdentified',
]);

/// @deprecated: use static config / ref data store
export const JobAuthorisationEnum = z.enum(['authorised', 'unauthorised']);

export const SlotTypeEnum = z.enum(['dropOff', 'fixedTime']);

export const IndustryEnablerTypeEnumSchema = z.enum([
  'Internal',
  'TireConnect',
  'HaynesProGb',
  'HaynesProAu',
  'TireCatalog',
  'Epicor',
]);

export const JobItemStatusEnum = z.enum([
  'Not Started',
  'In Progress',
  'Completed',
  'Cancelled',
]);

export const JobStatusEnum = z.enum([
  'Quoted Job',
  'Authorised',
  'To Do',
  'In Progress',
  'Complete',
  'Completed',
  'Cancelled',
  'In Progress - Awaiting QC',
  'In Progress - QC Failed',
  'In Progress - Overrunning',
  'Awaiting CAF',
  'Job Issues',
]);

export const JobItemAuthorisationEnum = z.enum([
  'Pending',
  'Approved',
  'Declined',
]);

export const OrderJobIdSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
});

export const JobsCreatedSchema = z.object({
  orderId: z.uuid(),
  jobIds: z.array(z.uuid()),
});

export const JobItemCreatedSchema = z
  .object({
    itemId: z.uuid(),
  })
  .extend(OrderJobIdSchema.shape);

export const JobItemPartCreatedSchema = z
  .object({
    partId: z.uuid(),
  })
  .extend(JobItemCreatedSchema.shape);

export const CreateEpicorJobRequestSchema = z.object({
  orderId: z.uuid(),
  xml: z.string(),
});

export const CreateJobSchema = z.object({
  orderId: z.uuid(),
  orderNumber: z.string().min(1),
  status: JobStatusEnum,
  creationSource: OrderCreationSourceEnum,
  fulfilmentBaseLocationId: z.uuid(),
  fulfilmentBaseLocationName: z.string(),
  fulfilmentType: z.string(),
  jobType: z.string().min(1).nullish(),
  jobDescription: z.string(),
  industryEnablerType: IndustryEnablerTypeEnumSchema.default(
    IndustryEnablerTypeEnumSchema.enum.Internal
  ).optional(),
  industryEnablerData: z.any().nullish(),
});

export const UpdateJobFulfilmentTypeSchema = OrderJobIdSchema.extend({
  fulfilmentType: z.string(),
});

export const CreateJobItemSchema = z.object({
  orderId: z.string(),
  jobId: z.string(),
  name: z.string(),
  status: JobItemStatusEnum,
  itemDescription: z.string(),
  schedulingConstraints: z.object({
    totalDuration: z.number(),
    assetConstraints: z.string(),
  }),
  reasonForAddition: z.object({
    description: z.string(),
    reasonCode: z.string(),
  }),
  industryEnablerType: IndustryEnablerTypeEnumSchema.default(
    IndustryEnablerTypeEnumSchema.enum.Internal
  ).optional(),
  industryEnablerData: z.any().nullish(),
});

export const CreateJobItemPartSchema = z.object({
  unitQuantity: z.number(),
  description: z.string(),
  partNumber: z.string(),
  sku: z.string(),
  supplier: z.object({
    supplierId: z.string(),
    supplierName: z.string(),
    stockCode: z.string(),
    leadTime: z.string(),
  }),
  productTaxCode: z.string(),
  unitBuyPrice: PriceSchema,
  unitSellPrice: UnitSellPricingSchema,
});

export const AddPartToJobItemSchema = CreateJobItemPartSchema.omit({
  productTaxCode: true,
  supplier: true,
  unitSellPrice: true,
  unitBuyPrice: true,
}).extend({
  unitSellPrice: UnitSellPricingSchema.omit({ discount: true }),
  duration: z.number(),
  supplier: z.object({
    supplierId: z.uuid(),
  }),
  status: z.string().nullish(),
});

export const UpdateJobCreationSourceSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  creationSource: OrderCreationSourceEnum,
});

///////////////////////
// ORDER JOB SCHEMAS //
export const OrderJobNotesFeSchema = z.object({
  id: z.uuid(),
  facingType: z.string().optional(),
  noteContent: z.string(),
});

export const OrderJobTechAppNotesNoteContentsSchema = z.object({
  id: z.string(),
  type: z.string(),
  meta: z.object({
    eventType: z.string(),
    note: z.array(
      z.object({
        title: z.string(),
        text: z.string(),
        images: z
          .array(
            z.object({
              height: z.number().optional(),
              width: z.number().optional(),
              type: z.string(),
              fileName: z.string(),
              fileSize: z.number().optional(),
              uri: z.string(),
            })
          )
          .optional()
          .default([]),
        createdAt: z.string(),
        facingType: z.string().optional(),
      })
    ),
  }),
  elements: z.string().nullish(),
});
export const OrderJobTechAppNotesSchema = z.object({
  noteDate: z.string(),
  noteContents: z.array(OrderJobTechAppNotesNoteContentsSchema),
});
export const OrderJobTechnicianSchema = z.object({
  userId: z.uuid(),
  name: z.string(),
  effortPercentage: z.number().min(0).max(100),
  qcGroupId: z.string().nullish(),
  qcRatingLevel: z.number().nonnegative(),
});
export const GeoLocationSchema = z.object({
  latitude: z.string().nullish(),
  longitude: z.string().nullish(),
});
export const OrderJobFulfilmentAddressFeSchema = z.object({
  id: z.uuid(),
  text: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  addressLine3: z.string().nullish(),
  addressLine4: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  timeZone: z.string().nullish(),
  geoLocation: GeoLocationSchema.nullish(),
  isDeleted: z.boolean(),
});
export const OrderJobPriceFeSchema = z.object({
  exTax: z.number(),
  incTax: z.number(),
  tax: z.number(),
  taxPercentage: z.number(),
});

export const OrderJobPartFeSchema = z.object({
  description: z.string(),
  fulfilmentDuration: z.number(),
  id: z.uuid(),
  industryEnablerData: z.any().nullish(),
  partNumber: z.string().nullish(),
  price: OrderJobPriceFeSchema,
  status: z.string().nullish(),
  unitQuantity: z.number(),
});
export const OrderJobItemFeSchema = z.object({
  description: z.string().nullish(),
  id: z.uuid(),
  industryEnablerData: z.any().nullish(),
  name: z.string().nullish(),
  parts: z.array(OrderJobPartFeSchema).default([]),
  price: OrderJobPriceFeSchema.nullish(),
  status: z.string().nullish(),
  totalDuration: z.number(),
});
export const OrderJobFeSchema = z.object({
  id: z.uuid(),
  fulfilmentLocationId: z.uuid(),
  fulfilmentAddress: OrderJobFulfilmentAddressFeSchema.nullish(),
  fulfilmentType: z.string().nullish(),
  jobType: z.string().nullish(),
  isSingleItem: z.boolean().nullish(),
  items: z.array(OrderJobItemFeSchema),
  status: z.string().nullish(),
  reason: z.string().nullish(),
  technicians: z.array(OrderJobTechnicianSchema),
  notes: z.array(OrderJobNotesFeSchema),
  creationSource: OrderCreationSourceEnum.optional(),
  industryEnablerData: z.any().nullish(),
  techAppNotes: OrderJobTechAppNotesSchema.optional(),
});
// END ORDER JOB SCHEMAS //
///////////////////////////

export const JobCostSummarySchema = z.object({
  totalExTax: z.number().nonnegative(),
  totalTax: z.number().nonnegative(),
  totalIncTax: z.number().nonnegative(),
});

export const AddJobItemAuthorisationSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  status: JobAuthorisationEnum.prefault('authorised'),
});

export const AddJobItemAuthorisationResponseSchema =
  AddJobItemAuthorisationSchema.omit({ status: true });

export const OrderJobNoteSchema = z.object({
  id: z.uuid(),
  date: z.coerce.date<Date>(),
  username: z.string(),
  userRole: z.string(),
  jobId: z.string(),
  description: z.string(),
  images: z.array(z.string()),
});

export const JobNoteListSchema = z.array(OrderJobNoteSchema);

export const JobCancelReasonsEnum = z.enum([
  'customerRequest',
  'jobNotRequired',
  'jobCreatedInError',
  'other',
]); // This should probably come from the static / dynamic config, but for now it's hardcoded - 16/09/24

export const JobBulkActionDisabledEnum = z.enum([
  'jobsAlreadyApproved',
  'jobsAreMobileFulfilment',
  'jobsMissingFulfilmentType',
  'jobsMissingJobType',
  'jobsNoStockAssociated',
  'jobsNotInValidStatus',
  'jobsNotScheduled',
  'jobsHaveTechniciansAlreadyAssigned',
]);

///// FRONTEND-BFF SCHEMAS....

export const PartsListFeSchema = z
  .object({
    key: z.string(),
  })
  .extend(
    OrderJobItemFeSchema.pick({
      id: true,
      description: true,
      status: true,
    }).shape
  );

export const GetJobPartRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
});

export const JobPartOrderedUnitsSchema = z.object({
  orderedUnitId: z.uuid(),
  purchaseOrder: z.string().nullish(),
  stockUnitId: z.string().nullish(),
  status: z.string(),
  supplierOrderReference: z.string().nullish(),
  supplierOrderStatus: z.string().nullish(),
  supplierOrderMessage: z.string().nullish(),
  supplierConfirmation: z.string().nullish(),
  orderedBuyPrice: PriceSchema.nullish(),
  fulfilment: z
    .object({
      fulfilmentId: z.uuid(),
      fulfilmentReady: z.boolean(),
      fulfilmentSuccess: z.boolean(),
      status: z.string().nullish(),
    })
    .nullish(),
});

export const SupplierDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  deliveryMethod: z
    .object({
      value: z.string(),
      text: z.string(),
    })
    .nullish(),
});

export const GetJobPartResponseSchema = z.object({
  id: z.uuid(),
  description: z.string(),
  partNumber: z.string().nullish(),
  sku: z.string().nullish(),
  unitQuantity: z.number(),
  unitsOrdered: z.number(),
  unitsFitted: z.number(),
  orderedUnits: JobPartOrderedUnitsSchema.array(),
  unitBuyPrice: PriceSchema,
  unitSellPrice: UnitSellPricingSchema,
  supplierDetails: SupplierDetailsSchema.nullish(),
});

export const DeleteJobPartResponseSchema = GetJobPartRequestSchema.omit({
  partId: true,
});

export const UpdateJobPartStatusRequestSchema = GetJobPartRequestSchema.extend({
  status: z.string(),
});

export const UpdateOrderJobItemPartQuantityRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
  quantity: z.number(),
});

export const UpdateOrderJobItemPartDescriptionSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
  description: z.string(),
});

export const UpdateOrderJobItemRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  name: z.string().nullish(),
  status: z.string(),
  itemDescription: z.string().nullish(),
  industryEnablerData: z.any().nullish(),
  schedulingConstraints: z.object({
    totalDuration: z.number(),
    assetConstraints: z.string(),
  }),
  warranty: WarrantySchema.array().nullish(),
});

export const UpdateOrderJobRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  jobDescription: z.string().nullish(),
  fulfilmentBaseLocation: z.uuid(),
  fulfilmentBaseLocationName: z.string().nullish(),
  fulfilmentBaseLocationCode: z.string().nullish(),
  fulfilmentType: z.string().nullish(),
  fulfilmentAddress: z.object({
    text: z.string().nullish(),
    addressLine1: z.string().nullish(),
    addressLine2: z.string().nullish(),
    addressLine3: z.string().nullish(),
    addressLine4: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
    countryCode: z.string().nullish(),
    timeZone: z.string().nullish(),
    geolocation: z.object({
      lat: z.string().nullish(),
      lng: z.string().nullish(),
    }),
  }),
  industryEnablerType: IndustryEnablerTypeEnumSchema,
  industryEnablerData: z.any().nullish(),
  jobType: z.string().nullish(),
  creationSource: OrderCreationSourceEnum,
});

export const UpdateOrderJobItemResponseSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
});

export const AddPartToJobItemRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  parts: z.array(CreateJobFromStockFormItemSchema),
});

export const MarkPartsAsNotFittedFormSchema = z.object({
  selectedParts: z.uuid().array(),
});
export const UpdatePartsFittedStatusdRequestSchema = z.object({
  parts: z
    .object({
      orderId: z.uuid(),
      jobId: z.uuid(),
      itemId: z.uuid(),
      partId: z.uuid(),
      fitted: z.boolean(),
    })
    .array(),
});

const JobItemAuthorisationSchema = z.object({
  jobId: z.uuid(),
  itemId: z.uuid(),
  state: JobItemAuthorisationEnum,
});

export const UpdateJobItemsAuthorisationRequestSchema = z.object({
  orderId: z.uuid(),
  authorisations: JobItemAuthorisationSchema.array(),
  state: JobItemAuthorisationEnum,
});

export const CancelJobFormSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  status: JobStatusEnum,
  reasonForChange: JobCancelReasonsEnum,
});

export const UpdateJobStatusSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  status: JobStatusEnum,
  reasonForChange: JobCancelReasonsEnum.optional(),
});

export const JobInformationOverridePartsSchema = z.object({
  partId: z.uuid(),
  unitSellPrice: UnitSellPricingSchema,
  description: z.string().nullish(),
  quantity: z.int(),
});

export const JobInformationJobItemSchema = z.object({
  jobId: z.uuid(),
  itemId: z.uuid(),
  description: z.string().nullish(),
  duration: z.number(),
});

export const JobInformationOverrideRequestSchema = z.object({
  orderId: z.uuid(),
  jobItem: JobInformationJobItemSchema,
  parts: JobInformationOverridePartsSchema.array().nullish(),
});

const GetOrderJobsNoteSchema = z.object({
  noteId: z.uuid(),
  noteContent: z.string().nullish(),
  facingType: NoteFacingTypeSchema,
  imageUrl: z.string().nullish(),
  dateAdded: z.string(),
  addedBy: z.string().nullish(),
});

const GetOrderJobsScheduleFulfilmentSchema = z.object({
  start: z.string().nullish(),
  end: z.string().nullish(),
  display: z.string().nullish(),
  slotType: z.string().nullish(),
  finalOptimisationComplete: z.boolean(),
});

const GetOrderJobsScheduleCustomerSchema = z.object({
  start: z.string().nullish(),
  startInvariantCulture: z.string().nullish(),
  end: z.string().nullish(),
  endInvariantCulture: z.string().nullish(),
  display: z.string().nullish(),
  slotType: z.string().nullish(),
  finalOptimisationComplete: z.boolean(),
});

const ProductCategoryCodeEnum = z.enum([
  'Parts',
  'Labor',
  'Tire',
  'Custom',
  'Battery',
  'Service',
  'Fee',
]);

const GetOrderJobItemsPartSchema = z.object({
  id: z.uuid(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  partNumber: z.string().nullish(),
  sku: z.string().nullish(),
  productTaxCode: z.string().nullish(),
  status: z.string().nullish(),
  unitQuantity: z.number(),
  numberUnitsOrdered: z.int(),
  numberUnitsFitted: z.int(),
  unitBuyPrice: PriceSchema,
  unitSellPrice: SellTotalSchema,
  productCategoryCode: ProductCategoryCodeEnum,
  additionalExpenses: z.object({}).nullish(),
  isFixedLabor: z.boolean(),
  industryEnablerType: IndustryEnablerTypeEnumSchema,
  industryEnablerData: z.any().nullish(),
});

export const GetOrderJobItemsJobItemSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
  itemId: z.uuid(),
  name: z.string().nullish(),
  status: z.string().nullish(),
  description: z.string().nullish(),
  authorised: z.boolean(),
  authorisation: JobItemAuthorisationEnum,
  authorisationStatus: z.string().nullish(),
  reasonForAddition: z.object({
    description: z.string().nullish(),
    reasonCode: z.string().nullish(),
  }),
  parts: z.array(GetOrderJobItemsPartSchema).nullish(),
  itemLineSellTotal: SellTotalSchema,
  schedulingConstraints: z.object({
    totalDuration: z.int(),
    assetConstraints: z.string().nullish(),
  }),
  industryEnablerData: z.any().nullish(),
});

export const GetOrderJobsJobSchema = z.object({
  jobId: z.uuid(),
  reference: z.string().nullish(),
  description: z.string().nullish(),
  fulfilmentLocation: z.uuid(),
  fulfilmentLocationCode: z.string().nullish(),
  status: z.string().nullish(),
  sellTotal: z.number().nullish(),
  dateScheduled: z.string().nullish(),
  dateCreated: z.string(),
  location: z.string().nullish(),
  fulfilmentType: z.string().nullish(),
  jobType: z.string().nullish(),
  creationSource: OrderCreationSourceEnum,
  scheduleCustomer: GetOrderJobsScheduleCustomerSchema,
  scheduleFulfilment: GetOrderJobsScheduleFulfilmentSchema,
  industryEnablerType: IndustryEnablerTypeEnumSchema,
  industryEnablerData: z.any().nullish(),
  items: z.array(GetOrderJobItemsJobItemSchema).nullish(),
  fulfilmentTechnicians: z.array(OrderJobTechnicianSchema).nullish(),
  notes: z.array(GetOrderJobsNoteSchema).nullish(),
  techAppNotes: OrderJobTechAppNotesSchema,
  discount: z.object({
    code: z.string(),
    exTax: z.number(),
    tax: z.number(),
  }),
  total: OrderTotalSchema,
});

export const GetOrderJobsResponseSchema = z.object({
  jobs: z.array(GetOrderJobsJobSchema).nullish(),
});

export const GetOrderJobItemsResponseSchema = z.object({
  items: z.array(GetOrderJobItemsJobItemSchema).nullish(),
});

export const UnallocateStockRequestSchema = z.object({
  orderId: z.uuid(),
  partId: z.uuid(),
});

export const BulkUpdateJobStatusEnum = z.enum(['Cancel', 'Complete']);
export const BulkUpdateJobStatusRequestSchema = z.object({
  orderId: z.uuid(),
  jobs: z.array(
    z.object({
      jobId: z.uuid(),
      updateType: BulkUpdateJobStatusEnum,
    })
  ),
  reasonForChange: JobCancelReasonsEnum.nullish(),
});

// types
export type JobStatusType = z.infer<typeof JobStatusEnum>;
export type JobCostSummaryModel = z.infer<typeof JobCostSummarySchema>;
export type PartsListFeModel = z.infer<typeof PartsListFeSchema>;
export type JobItemPartCreatedModel = z.infer<typeof JobItemPartCreatedSchema>;
export type JobItemAuthorisationType = z.infer<typeof JobItemAuthorisationEnum>;
export type OrderJobIdModel = z.infer<typeof OrderJobIdSchema>;

// types for OrderJobs FE
export type OrderJobPartFeModel = z.infer<typeof OrderJobPartFeSchema>;
export type OrderJobItemFeModel = z.infer<typeof OrderJobItemFeSchema>;
export type OrderJobFeModel = z.infer<typeof OrderJobFeSchema>;
export type OrderJobTechnicianModel = z.infer<typeof OrderJobTechnicianSchema>;
export type AddPartToJobItemRequestModel = z.infer<
  typeof AddPartToJobItemRequestSchema
>;
export type AddPartToJobItemModel = z.infer<typeof AddPartToJobItemSchema>;
export type CreateEpicorJobRequestModel = z.infer<
  typeof CreateEpicorJobRequestSchema
>;
export type AddJobItemAuthorisationModel = z.infer<
  typeof AddJobItemAuthorisationSchema
>;
export type AddJobItemAuthorisationResponseModel = z.infer<
  typeof AddJobItemAuthorisationResponseSchema
>;
export type JobsCreatedModel = z.infer<typeof JobsCreatedSchema>;
export type JobItemCreatedModel = z.infer<typeof JobItemCreatedSchema>;
export type CreateJobModel = z.infer<typeof CreateJobSchema>;
export type CreateJobItemModel = z.infer<typeof CreateJobItemSchema>;
export type CreateJobItemPartModel = z.infer<typeof CreateJobItemPartSchema>;
export type JobNoteListModel = z.infer<typeof JobNoteListSchema>;
export type OrderJobTechAppNoteContentFeModel = z.infer<
  typeof OrderJobTechAppNotesNoteContentsSchema
>;
export type GetJobPartRequestModel = z.infer<typeof GetJobPartRequestSchema>;
export type GetJobPartResponseModel = z.infer<typeof GetJobPartResponseSchema>;
export type JobPartOrderedUnitsModel = z.infer<
  typeof JobPartOrderedUnitsSchema
>;
export type UpdateJobFulfilmentTypeModel = z.infer<
  typeof UpdateJobFulfilmentTypeSchema
>;
export type DeleteJobPartResponseModel = z.infer<
  typeof DeleteJobPartResponseSchema
>;
export type UpdateJobPartStatusRequestModel = z.infer<
  typeof UpdateJobPartStatusRequestSchema
>;

export type UpdateOrderJobItemPartQuantityRequestModel = z.infer<
  typeof UpdateOrderJobItemPartQuantityRequestSchema
>;

export type UpdateOrderJobItemPartDescriptionModel = z.infer<
  typeof UpdateOrderJobItemPartDescriptionSchema
>;

export type UpdateOrderJobItemResponseModel = z.infer<
  typeof UpdateOrderJobItemResponseSchema
>;

export type UpdateOrderJobItemRequestModel = z.infer<
  typeof UpdateOrderJobItemRequestSchema
>;
export type JobCancelReasonsType = z.infer<typeof JobCancelReasonsEnum>;

export type UpdatePartsFittedStatusRequestModel = z.infer<
  typeof UpdatePartsFittedStatusdRequestSchema
>;
export type MarkPartsAsNotFittedFormModel = z.infer<
  typeof MarkPartsAsNotFittedFormSchema
>;

export type UpdateJobItemsAuthorisationRequestModel = z.infer<
  typeof UpdateJobItemsAuthorisationRequestSchema
>;
export type UpdateJobStatusModel = z.infer<typeof UpdateJobStatusSchema>;
export type UpdateJobCreationSourceModel = z.infer<
  typeof UpdateJobCreationSourceSchema
>;
export type JobInformationJobItemModel = z.infer<
  typeof JobInformationJobItemSchema
>;
export type JobInformationOverrideRequestModel = z.infer<
  typeof JobInformationOverrideRequestSchema
>;
export type GetOrderJobsResponseModel = z.infer<
  typeof GetOrderJobsResponseSchema
>;
export type UpdateOrderJobRequestModel = z.infer<
  typeof UpdateOrderJobRequestSchema
>;
export type OrderJobItemsJobItemModel = z.infer<
  typeof GetOrderJobItemsJobItemSchema
>;
export type GetOrderJobItemsResponseModel = z.infer<
  typeof GetOrderJobItemsResponseSchema
>;
export type UnallocateStockRequestModel = z.infer<
  typeof UnallocateStockRequestSchema
>;
export type BulkUpdateJobStatusType = z.infer<typeof BulkUpdateJobStatusEnum>;
export type BulkUpdateJobStatusRequestModel = z.infer<
  typeof BulkUpdateJobStatusRequestSchema
>;
export type JobBulkActionDisabledType = z.infer<
  typeof JobBulkActionDisabledEnum
>;
